import cn from 'classnames';
import { useCart } from 'contexts/cart';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import { isProductIdInCart } from 'hooks/product/utils';
import { isGiftProductItem } from 'interfaces/line-item';
import { CollectionProduct } from 'models/collection/collection';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useProductDetailsStore } from 'stores/product-details';
import { useTrackingStore } from 'stores/tracking';
import { Gtm } from 'tracking';
import Content from './content';
import InCartExperimentBadge from './in-cart-experiment';

const PriceVariantButton = dynamic(() => import('./price-variant-button'), {
  ssr: false,
});

export const CARD_TYPES = {
  SLIDER: 'slider',
  SLIDER_LARGE: 'slider large',
  REGULAR: 'regular',
} as const;

export type CARD_TYPES = (typeof CARD_TYPES)[keyof typeof CARD_TYPES];

interface PropTypes extends React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> {
  /** Collection Title for tracking */
  collectionTitle: string;
  isBestSeller?: boolean;
  position: number;
  product: CollectionProduct;
  shouldShowBlackRibbon?: boolean;
  showSubscriptionPrice?: boolean;
  starSize?: 'small' | 'medium';
  trackingElementName?: string | string[];
  type?: CARD_TYPES;
  onTileClick?: () => void;
  /** Url query to be added  */
  additionalQuery?: Record<string, string>;
}

/**
 * Product Card. Wrapper for Product Card Content.
 * Includes tracking and navigation.
 */
const ProductCard: React.FC<PropTypes> = ({
  className,
  collectionTitle,
  isBestSeller,
  position,
  product,
  shouldShowBlackRibbon,
  showSubscriptionPrice = false,
  starSize,
  trackingElementName,
  type = CARD_TYPES.REGULAR,
  onTileClick,
  additionalQuery,
}) => {
  const { id: productId, title: productTitle, handle } = product;
  const { setTrackedList } = useTrackingStore();

  const onGoToProduct = useCallback(() => {
    if (trackingElementName) {
      Gtm.track.selectElement({
        element: trackingElementName,
      });
    }

    const { tags, productType } = product;
    Gtm.track({
      group: Gtm.Group.Product,
      name: Gtm.ProductEvent.Click,
      data: {
        product: {
          productTitle,
          productId,
          productType,
          tags: tags.join(','),
          position,
          list: collectionTitle || '',
        },
      },
    });
    setTrackedList(productId, collectionTitle || '');
  }, [
    productId,
    productTitle,
    collectionTitle,
    position,
    product,
    trackingElementName,
    setTrackedList,
  ]);

  const promotedVariant = useMemo(
    () => product.variants.find((variant) => variant.id === product.promoteVariantId),
    [product]
  );

  const link = useMemo(() => {
    let queryObj = {};
    if (product.promoteVariantId && promotedVariant?.availableForSale) {
      queryObj = {
        ...queryObj,
        variant: product.promoteVariantId,
      };
    }
    if (additionalQuery) {
      queryObj = {
        ...queryObj,
        ...additionalQuery,
      };
    }
    const searchParam = new URLSearchParams(queryObj);

    return `/products/${handle}${searchParam.size > 0 ? '?' + searchParam.toString() : ''}`;
  }, [additionalQuery, handle, product.promoteVariantId, promotedVariant?.availableForSale]);

  const { currentOrderType } = useProductDetailsStore();
  const { isEc } = useIsExistingCustomer();
  const { couponCode, items } = useCart();

  const isInCart = useMemo(
    () =>
      isProductIdInCart({
        id: productId,
        cartItems: items.filter((line) => !isGiftProductItem(line)),
      }),
    [items, productId]
  );

  const additionalPriceInfo = useMemo(() => {
    const subCouponDiscount = product.variantPrice?.subscription?.discounts.find(
      (discount) => discount.origin === 'coupon'
    );

    const oneOffCouponDiscount = product.variantPrice?.one_off?.discounts.find(
      (discount) => discount.origin === 'coupon'
    );

    const contentfulDiscount = product.variantPrice?.subscription?.discounts.find(
      (discount) => discount.origin === 'contentful'
    );

    const subscriptionDiscount = product.variantPrice?.subscription?.discounts.find(
      (discount) => discount.origin === 'subscription'
    );

    return (
      <>
        {(subscriptionDiscount?.value || contentfulDiscount?.value) && (
          <>
            <FormattedMessage
              id="cdp:ab-test:product-card:additional-price-info"
              values={{
                percentage: subscriptionDiscount?.value || contentfulDiscount?.value,
              }}
            />
          </>
        )}
        {(subscriptionDiscount?.value || contentfulDiscount?.value) &&
          (subCouponDiscount || oneOffCouponDiscount) &&
          couponCode && (
            <>
              {' '}
              <FormattedMessage id="common:and" />{' '}
            </>
          )}
        {(subCouponDiscount || oneOffCouponDiscount) && couponCode && (
          <FormattedMessage
            id="cdp:ab-test:product-card:additional-price-info:coupon"
            values={{
              couponCode,
            }}
          />
        )}
      </>
    );
  }, [
    product.variantPrice?.one_off?.discounts,
    product.variantPrice?.subscription?.discounts,
    couponCode,
  ]);

  const renderInCartBadge = useMemo(
    () =>
      isInCart
        ? (wrap?: (badge: React.ReactNode) => React.ReactNode) => (
            <InCartExperimentBadge type={type} wrap={wrap} />
          )
        : undefined,
    [isInCart, type]
  );

  const content = (
    <Content
      isBestSeller={isBestSeller}
      product={product}
      showSubscriptionPrice={showSubscriptionPrice}
      starSize={starSize}
      type={type}
      shouldShowBlackRibbon={shouldShowBlackRibbon}
      orderType={currentOrderType}
      additionalPriceInfo={additionalPriceInfo}
      renderInCartBadge={renderInCartBadge}
    />
  );

  return (
    <div className="relative flex h-full flex-1" data-test="product-card-wrapper">
      {onTileClick ? (
        <button
          className={cn(
            'focus-default flex h-full w-full decoration-transparent',
            className,
            isEc && 'pb-8'
          )}
          data-test="product-card-tile"
          onClick={onTileClick}
        >
          {content}
        </button>
      ) : (
        <Link
          href={link}
          className={cn(
            'focus-default flex h-full w-full decoration-transparent',
            className,
            isEc && 'pb-8'
          )}
          data-test="product-card-tile"
          onClick={onGoToProduct}
        >
          {content}
        </Link>
      )}
      {product.availableForSale && isEc && (
        <PriceVariantButton
          productId={product.id}
          variantLength={product.variantLength}
          productHandle={product.handle}
        />
      )}
    </div>
  );
};

export default ProductCard;
